import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { CheckboxOption } from '../../model/CheckboxOption';
import { LocationBound } from '../../model/Location';
import { GET_LISTINGS_AND_STOREFRONTS_STR } from './marketPlaceQueries';
import { generateListingFilters, getStorefrontFilters } from '../../util/AppUtil';
import { flattenObj } from '../../util/flattenObj';
import { customizeListings, customizeStorefronts } from './transform';

// Cap how many pins one map fetch will pull. The map previously requested
// limit: -1 — every listing AND every storefront in the viewport, uncapped —
// which is a main driver of the 1GB API instance returning 503s under load as
// the listing count grows. Clustering already collapses dense areas, so a high
// cap is invisible to users while bounding the worst-case query cost. The
// headline "N listings near you" count is computed by the separate list query,
// not this one, so it stays accurate. Tune this number if needed.
// Exported so the map can show a "1,000+ in view" hint at the cap (plan B5).
export const MAP_MARKER_LIMIT = 1000;

// Different sellers routinely share the EXACT same stored coordinate: picking
// "Toronto, Ontario" from the location dropdown stores the city centroid, and
// a profile location set that way pre-fills every listing the account creates.
// Grouping only by coordinate silently merged those sellers into one pin that
// read as a single listing (Luke's report: "different sellers in nearby areas
// are merging together"). So: group by coordinate, then SUB-GROUP by owner.
// One owner keeps the merged pin + popup card slider (their own items at one
// spot); multiple owners fan out on a small deterministic ring around the
// shared point, so each seller gets their own pin and the cluster layer shows
// an honest numbered badge at city zoom instead of a lone masquerading pin.
const OWNER_RING_METERS = 150;

// Ring placement for the i-th of n owners sharing a coordinate. The phase is
// seeded from the coordinate (same trick as the Antarctica scatter) so
// different cities fan in different directions but every load of the same
// data produces the same pins - no jumping between visits.
const ringLatLng = (lat: number, lng: number, index: number, count: number): [number, number] => {
  const phase = 2 * Math.PI * Math.abs((Math.sin(lat * 12.9898 + lng * 78.233) * 43758.5453) % 1);
  const angle = phase + (2 * Math.PI * index) / count;
  const dLat = (OWNER_RING_METERS * Math.sin(angle)) / 111320;
  // Longitude degrees shrink toward the poles; clamp so the math stays sane
  // at extreme latitudes (no real city sits past 78 degrees anyway).
  const cosLat = Math.max(0.2, Math.cos((lat * Math.PI) / 180));
  const dLng = (OWNER_RING_METERS * Math.cos(angle)) / (111320 * cosLat);
  return [lat + dLat, lng + dLng];
};

const transformDataToGeojson = (data: any) => {
  const features: any[] = [];
  const groups: any = {};
  for (const item of data) {
    const lat = item?.location?.latitude;
    const lng = item?.location?.longitude;
    // Skip location-less entries instead of building a NaN pin that would
    // throw "Invalid LatLng" inside Leaflet (online-only shops render via
    // their own Online Continent path, not here).
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      continue;
    }
    const key = lat + ',' + lng;
    if (groups[key]) {
      groups[key].push(item);
    } else {
      groups[key] = [item];
    }
  }
  for (const key in groups) {
    const arr = groups[key];
    const [lat, lng] = key.split(',').map(Number);
    // Sub-group the same-coordinate stack by owner. Ownerless entries (edge
    // case: detached account) each count as their own group so they never
    // silently merge with someone else's items.
    const byOwner: any = {};
    for (const item of arr) {
      const ownerKey =
        item?.user?.id != null ? 'u' + item.user.id : 'x-' + (item?.type ?? '') + '-' + item?.id;
      if (byOwner[ownerKey]) {
        byOwner[ownerKey].push(item);
      } else {
        byOwner[ownerKey] = [item];
      }
    }
    // Sorted so ring order (and therefore each pin's spot) is stable for a
    // given set of sellers regardless of API response order.
    const ownerKeys = Object.keys(byOwner).sort();
    ownerKeys.forEach((ownerKey, index) => {
      const coordinates =
        ownerKeys.length > 1 ? ringLatLng(lat, lng, index, ownerKeys.length) : [lat, lng];
      features.push({
        type: 'Feature',
        properties: byOwner[ownerKey],
        geometry: {
          coordinates,
          type: 'Point'
        }
      });
    });
  }

  const geojsonData = {
    type: 'FeatureCollection',
    features
  };

  return data ? JSON.stringify(geojsonData) : null;
};

export const useGetMarketPlaceMapData = (
  moreFilter: any,
  categories: CheckboxOption[] | undefined,
  searchText: string,
  locationBounds: LocationBound,
  listingOffset: number,
  storefrontOffset: number,
  order?: string,
  userId?: string
) => {
  const [geoData, setGeoData] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState(null);

  // Remembers the last request payload so we skip duplicate fetches — e.g. the
  // default-bounds → resolved-bounds churn on load, or an identical bounds
  // dispatch. Set before the request so an in-flight duplicate is suppressed
  // too; cleared on error so a failed request can be retried.
  const lastRequestKeyRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    const type = 'map';
    let skip = true;
    const { maxLat, maxLng, minLat, minLng } = locationBounds ?? ({} as LocationBound);
    // Category discovery fix: never fire an unbounded query. A transient
    // render with missing bounds used to fetch the WHOLE WORLD unfiltered
    // and repaint the map over the user's active category selection.
    if (![maxLat, maxLng, minLat, minLng].every((edge) => Number.isFinite(edge))) {
      return;
    }
    const categoryIds =
      categories?.length ?? -1 > 0
        ? categories?.map((category) => category.value)
        : moreFilter?.categories?.map((category: any) => category.value);
    const types = moreFilter?.listings?.map((listing: any) => listing.value);
    const alternatePriceOptions = moreFilter?.alternatePriceOptions?.map(
      (alternatePrice: any) => alternatePrice.value
    );
    const priceType = moreFilter?.priceType?.map((priceType: any) => priceType.value);
    const shippable = moreFilter?.shippableOnly ? true : undefined;
    if (userId) {
      skip = false;
    }
    const limit = MAP_MARKER_LIMIT;

    const variables = {
      type,
      listingOffset,
      storefrontOffset,
      limit,
      order,
      searchText,
      categoryIds,
      types,
      maxLat,
      maxLng,
      minLat,
      minLng,
      alternatePriceOptions,
      priceType,
      shippable,
      userId,
      skip
    };

    const conditions = {
      filters: generateListingFilters(variables),
      storefrontFilters: getStorefrontFilters(variables)
    };

    const requestVariables = { ...variables, ...conditions };
    const requestKey = JSON.stringify(requestVariables);
    // Same bounds + filters as the last request — skip the redundant fetch.
    if (requestKey === lastRequestKeyRef.current) {
      return;
    }
    lastRequestKeyRef.current = requestKey;

    const token = localStorage.getItem('token');
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: token ? `Bearer ${token}` : ''
      },
      body: JSON.stringify({
        query: GET_LISTINGS_AND_STOREFRONTS_STR,
        variables: requestVariables
      })
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_STRAPI_API_URL}/graphql`,
        requestOptions
      );
      const responseData = await response.json();

      const listings = flattenObj(responseData?.data?.listings);
      const storefronts = flattenObj(responseData?.data?.storeFronts);
      const customizedListings = customizeListings(listings);
      const customizedStorefronts = customizeStorefronts(storefronts);
      const geojsonData = transformDataToGeojson([
        ...(customizedListings || []),
        ...(customizedStorefronts || [])
      ]);

      setGeoData(geojsonData ?? '');
      setLoading(false);
      setError(null);
    } catch (error: any) {
      lastRequestKeyRef.current = null;
      setLoading(false);
      setError(error.message || 'An error occurred');
    }
  }, [
    moreFilter,
    categories,
    searchText,
    locationBounds,
    listingOffset,
    storefrontOffset,
    order,
    userId
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return useMemo(
    () => ({
      geoData,
      loading,
      error
    }),
    [geoData, loading, error]
  );
};
